import { Pause, Play, Radio, Volume2, VolumeX } from "lucide-react";

import { Image } from "@/components/image";
import { Link } from "@/components/link";
import { usePlayer } from "@/components/providers/player-provider";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { formatTime } from "@/lib/audio-utils";

export function GlobalPlayerBar() {
  const {
    current,
    currentTime,
    duration,
    playLive,
    playing,
    seek,
    setVolume,
    toggle,
    volume,
  } = usePlayer();

  if (!current) {
    return null;
  }

  const isRecording = current.kind === "recording";
  const title = isRecording ? current.title : "OSCAR Radio";
  const subtitle = isRecording ? current.showName : "Live stream";
  const artUrl = isRecording ? current.iconUrl : null;
  const episodeHref = isRecording
    ? `/view/episodes/${current.episodeId}`
    : null;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const art = (
    <div className="relative size-12 shrink-0 overflow-hidden rounded-xl bg-primary/5 ring-1 ring-inset ring-black/5">
      {artUrl ? (
        <Image
          fill
          src={artUrl}
          alt={`${title} art`}
          className="object-cover"
        />
      ) : (
        <span className="flex size-full items-center justify-center text-primary">
          <Radio className="size-5" />
        </span>
      )}
    </div>
  );

  const info = (
    <div className="min-w-0 space-y-1">
      <p className="truncate text-sm font-semibold" title={title}>
        {title}
      </p>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {!isRecording && (
          <span
            aria-hidden="true"
            className="size-1.5 shrink-0 rounded-full bg-primary"
          />
        )}
        <p className="truncate" title={subtitle}>
          {subtitle}
        </p>
      </div>
    </div>
  );

  return (
    <section
      data-player-bar
      data-player-kind={current.kind}
      aria-label="Audio player"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border/70 bg-background/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_32px_-16px_rgba(0,0,0,0.15)] backdrop-blur-xl"
    >
      <div className="mx-auto grid w-full max-w-7xl grid-cols-[minmax(0,1fr)_auto] items-center gap-x-5 gap-y-3 px-5 py-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] lg:px-10">
        {episodeHref ? (
          <Link
            className="group flex min-w-0 items-center gap-3 hover:[&_p:first-of-type]:underline"
            href={episodeHref}
          >
            {art}
            {info}
          </Link>
        ) : (
          <div className="flex min-w-0 items-center gap-3">
            {art}
            {info}
          </div>
        )}

        <div className="flex items-center justify-center">
          <Button
            aria-label={playing ? "Pause" : "Play"}
            className="size-11 shrink-0 rounded-full shadow-sm"
            onClick={() => {
              toggle();
            }}
            size="icon"
          >
            {playing ? (
              <Pause className="size-5 fill-current" />
            ) : (
              <Play className="size-5 translate-x-px fill-current" />
            )}
          </Button>
        </div>

        <div className="hidden min-w-0 items-center justify-end gap-4 sm:flex">
          {isRecording && (
            <Button
              className="shrink-0"
              onClick={() => {
                playLive();
              }}
              size="sm"
              variant="outline"
            >
              <Radio className="size-4" />
              Go live
            </Button>
          )}
          <div className="flex shrink-0 items-center gap-3">
            {volume === 0 ? (
              <VolumeX
                aria-hidden="true"
                className="size-4 shrink-0 text-muted-foreground"
              />
            ) : (
              <Volume2
                aria-hidden="true"
                className="size-4 shrink-0 text-muted-foreground"
              />
            )}
            <div className="w-16 lg:w-24">
              <Slider
                aria-label="Volume"
                max={100}
                min={0}
                step={1}
                value={[volume]}
                onValueChange={(values) => setVolume(values[0])}
              />
            </div>
          </div>
        </div>

        {isRecording && (
          <div className="col-span-full mx-auto flex w-full max-w-xl items-center gap-3">
            <span className="w-11 shrink-0 text-end text-xs text-muted-foreground tabular-nums">
              {formatTime(currentTime)}
            </span>
            <Slider
              aria-label="Seek"
              className="min-w-0 flex-1"
              max={100}
              min={0}
              step={0.1}
              value={[progress]}
              onValueChange={(values) => seek(values[0] / 100)}
            />
            <span className="w-11 shrink-0 text-xs text-muted-foreground tabular-nums">
              {formatTime(duration)}
            </span>
            <Button
              aria-label="Go live"
              className="sm:hidden"
              onClick={() => {
                playLive();
              }}
              size="icon-sm"
              variant="outline"
            >
              <Radio className="size-4" />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
